import React, { useState } from 'react';
import { Kelas } from '../../types/index.js';
import { Plus, Edit2, Trash2, Search, Check, X } from 'lucide-react';
import { useDialog } from '../ui/DialogProvider.js';
import { useDataTable } from '../../hooks/useDataTable.js';
import { Pagination } from '../ui/Pagination.js';
import { SortableHeader } from '../ui/SortableHeader.js';

interface KelasViewProps {
  kelasList: Kelas[];
  onAddKelas: (data: Partial<Kelas>) => Promise<void>;
  onUpdateKelas: (id: string, data: Partial<Kelas>) => Promise<void>;
  onDeleteKelas: (id: string) => Promise<void>;
}

export const KelasView: React.FC<KelasViewProps> = ({
  kelasList,
  onAddKelas,
  onUpdateKelas,
  onDeleteKelas,
}) => {
  const { showAlert, showConfirm } = useDialog();
  const [tingkat, setTingkat] = useState('1');
  const [waliKelas, setWaliKelas] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<Kelas | null>(null);
  const [nama, setNama] = useState('');

  const {
    searchQuery: search,
    setSearchQuery: setSearch,
    sortBy,
    sortDir,
    handleSort,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalPages,
    totalRows,
    paginatedData: filteredKelas,
  } = useDataTable<Kelas>({
    data: kelasList,
    searchFields: ['nama', 'waliKelas'],
    initialSortBy: 'nama',
  });

  const handleOpenAdd = () => {
    setEditItem(null);
    setNama('');
    setTingkat('1');
    setWaliKelas('');
    setShowModal(true);
  };

  const handleOpenEdit = (item: Kelas) => {
    setEditItem(item);
    setNama(item.nama);
    setTingkat(String(item.tingkat));
    setWaliKelas(item.waliKelas);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nama) return;
    if (editItem) {
      await onUpdateKelas(editItem.id, { nama, tingkat: Number(tingkat), waliKelas });
    } else {
      await onAddKelas({ nama, tingkat: Number(tingkat), waliKelas });
    }
    setShowModal(false);
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      {/* Title & Action Bar (Matching Document Page 2) */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-bold text-slate-900">Manajemen Kelas</h2>
          <p className="text-xs text-slate-500">Data rombongan belajar dan wali kelas SDMT Ponorogo</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sky-500 w-48"
            />
          </div>

          <button
            onClick={handleOpenAdd}
            className="flex items-center gap-1.5 bg-sky-600 hover:bg-sky-500 text-white text-xs font-semibold px-3.5 py-1.5 rounded-md transition shadow-xs"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Tambah</span>
          </button>
        </div>
      </div>

      {/* Table (Replicating Document Page 2) */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden flex flex-col min-h-[400px]">
        <div className="overflow-x-auto flex-1">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase sticky top-0 z-10">
              <tr>
                <th className="py-2.5 px-4 w-32">Aksi</th>
                <th className="py-2.5 px-4 w-16 text-center">No</th>
                <SortableHeader label="Tingkat" columnKey="tingkat" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Nama Kelas" columnKey="nama" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Wali Kelas" columnKey="waliKelas" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Jumlah Siswa" columnKey="jumlahSiswa" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} className="text-center" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredKelas.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-slate-400">
                    Tidak ada data kelas yang cocok
                  </td>
                </tr>
              ) : (
                filteredKelas.map((item, index) => (
                  <tr key={item.id} className="hover:bg-sky-50/50 transition">
                    <td className="py-2 px-4">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleOpenEdit(item)}
                          className="flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded border border-emerald-300 transition"
                        >
                          <Edit2 className="w-3 h-3" />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={async () => {
                            if (await showConfirm(`Hapus kelas ${item.nama}?`, { title: 'Hapus Kelas', type: 'danger' })) onDeleteKelas(item.id);
                          }}
                          className="flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-rose-50 text-rose-700 hover:bg-rose-100 rounded border border-rose-300 transition"
                        >
                          <Trash2 className="w-3 h-3" />
                          <span>Del</span>
                        </button>
                      </div>
                    </td>
                    <td className="py-2 px-4 text-slate-500 font-mono text-center">{(currentPage - 1) * rowsPerPage + index + 1}</td>
                    <td className="py-2 px-4 font-bold text-slate-800 text-center">{item.tingkat}</td>
                    <td className="py-2 px-4 font-semibold text-sky-800">{item.nama}</td>
                    <td className="py-2 px-4 text-slate-600">{item.waliKelas || '-'}</td>
                    <td className="py-2 px-4 text-center">
                      <span className="bg-sky-100 text-sky-800 font-bold px-2 py-0.5 rounded-full text-[10px]">
                        {item.jumlahSiswa || 0} Siswa
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination 
          currentPage={currentPage}
          totalPages={totalPages}
          totalRows={totalRows}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={setRowsPerPage}
        />
      </div>

      {/* Modal Tambah / Edit */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 border border-slate-200">
            <h3 className="text-sm font-bold text-slate-900 mb-4 pb-2 border-b">
              {editItem ? 'Edit Data Kelas' : 'Tambah Kelas Baru'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4 text-xs">
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Nama Kelas</label>
                <input
                  type="text"
                  placeholder="Contoh: 1 AL BAYAN"
                  value={nama}
                  onChange={(e) => setNama(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-sky-500"
                  required
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Tingkat</label>
                <select
                  value={tingkat}
                  onChange={(e) => setTingkat(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-sky-500 bg-white"
                >
                  <option value="1">Tingkat 1 (Kelas 1)</option>
                  <option value="2">Tingkat 2 (Kelas 2)</option>
                  <option value="3">Tingkat 3 (Kelas 3)</option>
                  <option value="4">Tingkat 4 (Kelas 4)</option>
                  <option value="5">Tingkat 5 (Kelas 5)</option>
                  <option value="6">Tingkat 6 (Kelas 6)</option>
                </select>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Wali Kelas</label>
                <input
                  type="text"
                  placeholder="Contoh: Ustadzah Siti Aminah, S.Pd"
                  value={waliKelas}
                  onChange={(e) => setWaliKelas(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-sky-500"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-3 py-1.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md font-medium"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-sky-600 hover:bg-sky-500 text-white rounded-md font-semibold"
                >
                  Simpan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
